import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { ScrollArea } from "../components/ui/scroll-area";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { 
  CheckSquare, Loader2, Save, RotateCcw, 
  FileText, Users, Package, Receipt, Wallet, Truck, UserCheck,
  ClipboardList, ChevronRight
} from "lucide-react";

const MODULO_ICONS = {
  gestion_humana: Users,
  visitantes: UserCheck,
  inventario: Package,
  requisiciones: ClipboardList,
  cuentas_pagar: Receipt,
  caja_chica: Wallet,
  mensajeria: Truck
};

export default function CamposObligatoriosPage() {
  const [estructura, setEstructura] = useState({});
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cambiosPendientes, setCambiosPendientes] = useState({});
  const [moduloExpandido, setModuloExpandido] = useState("");
  
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador"].includes(user?.role?.toLowerCase());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [estructuraRes, configsRes] = await Promise.all([
        axios.get(`${API}/campos-obligatorios/estructura`, getAuthHeaders()),
        axios.get(`${API}/campos-obligatorios`, getAuthHeaders())
      ]);
      setEstructura(estructuraRes.data || {});
      setConfigs(configsRes.data.configs || {});
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getCampoObligatorio = (modulo, formulario, campoId, defaultValue) => {
    const key = `${modulo}_${formulario}`;
    // Check pending changes first
    if (cambiosPendientes[key]?.[campoId] !== undefined) {
      return cambiosPendientes[key][campoId];
    }
    // Then check saved config
    if (configs[key]?.[campoId] !== undefined) {
      return configs[key][campoId];
    }
    return defaultValue;
  };

  const handleToggleCampo = (modulo, formulario, campoId, currentValue) => {
    const key = `${modulo}_${formulario}`;
    setCambiosPendientes(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [campoId]: !currentValue
      }
    }));
  };

  const handleGuardarModulo = async (modulo, formulario) => {
    const key = `${modulo}_${formulario}`;
    const cambios = cambiosPendientes[key];
    if (!cambios || Object.keys(cambios).length === 0) {
      toast.info("No hay cambios para guardar");
      return;
    }

    setSaving(true);
    try {
      const campos = Object.entries(cambios).map(([campo_id, obligatorio]) => ({
        campo_id,
        obligatorio
      }));

      await axios.put(`${API}/campos-obligatorios/${modulo}/${formulario}`, {
        modulo,
        formulario,
        campos
      }, getAuthHeaders());

      // Update local configs
      setConfigs(prev => ({
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          ...cambios
        }
      }));

      // Clear pending changes for this form
      setCambiosPendientes(prev => {
        const newPending = { ...prev };
        delete newPending[key];
        return newPending;
      });

      toast.success("Configuración guardada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleResetModulo = async (modulo, formulario) => {
    if (!confirm("¿Restablecer todos los campos a sus valores por defecto?")) return;

    setSaving(true);
    try {
      await axios.post(`${API}/campos-obligatorios/reset/${modulo}/${formulario}`, {}, getAuthHeaders());
      
      // Clear local configs for this form
      const key = `${modulo}_${formulario}`;
      setConfigs(prev => {
        const newConfigs = { ...prev };
        delete newConfigs[key];
        return newConfigs;
      });
      setCambiosPendientes(prev => {
        const newPending = { ...prev };
        delete newPending[key];
        return newPending;
      });

      toast.success("Configuración restablecida");
    } catch (err) {
      toast.error("Error al restablecer");
    } finally {
      setSaving(false);
    }
  };

  const tienesCambiosPendientes = (modulo, formulario) => {
    const key = `${modulo}_${formulario}`;
    return cambiosPendientes[key] && Object.keys(cambiosPendientes[key]).length > 0;
  };

  const contarCamposObligatorios = (modulo, formulario, campos) => {
    let count = 0;
    campos.forEach(campo => {
      if (getCampoObligatorio(modulo, formulario, campo.id, campo.default_obligatorio)) {
        count++;
      }
    });
    return count;
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden">
        <div className="max-w-5xl">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <CheckSquare className="w-7 h-7 text-green-600" />Campos Obligatorios
            </h1>
            <p className="text-slate-500 mt-1">Configure qué campos son requeridos en cada formulario del sistema</p>
          </div>

          {loading ? (
            <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin" /></div>
          ) : (
            <Accordion type="single" collapsible value={moduloExpandido} onValueChange={setModuloExpandido}>
              {Object.entries(estructura).map(([moduloKey, modulo]) => {
                const IconComponent = MODULO_ICONS[moduloKey] || FileText;
                return (
                  <AccordionItem key={moduloKey} value={moduloKey} className="mb-4">
                    <Card>
                      <AccordionTrigger className="px-6 py-4 hover:no-underline">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                            <IconComponent className="w-5 h-5 text-slate-600" />
                          </div>
                          <div className="text-left">
                            <h3 className="font-semibold text-lg">{modulo.label}</h3>
                            <p className="text-sm text-slate-500">
                              {Object.keys(modulo.formularios).length} formularios
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="px-6 pb-6 space-y-4">
                          {Object.entries(modulo.formularios).map(([formKey, form]) => {
                            const obligatorios = contarCamposObligatorios(moduloKey, formKey, form.campos);
                            const hasPending = tienesCambiosPendientes(moduloKey, formKey);
                            
                            return (
                              <Card key={formKey} className={`border ${hasPending ? 'border-amber-300 bg-amber-50/50' : ''}`}>
                                <CardHeader className="pb-3">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <CardTitle className="text-base flex items-center gap-2">
                                        <FileText className="w-4 h-4" />
                                        {form.label}
                                      </CardTitle>
                                      <CardDescription>
                                        {obligatorios} de {form.campos.length} campos obligatorios
                                      </CardDescription>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {hasPending && (
                                        <Badge variant="outline" className="border-amber-400 text-amber-700">
                                          Cambios sin guardar
                                        </Badge>
                                      )}
                                      {isAdmin && (
                                        <>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleResetModulo(moduloKey, formKey)}
                                            disabled={saving}
                                          >
                                            <RotateCcw className="w-4 h-4 mr-1" />Reset
                                          </Button>
                                          <Button
                                            size="sm"
                                            onClick={() => handleGuardarModulo(moduloKey, formKey)}
                                            disabled={saving || !hasPending}
                                            className="bg-green-600 hover:bg-green-700 text-white"
                                          >
                                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                                            Guardar
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </CardHeader>
                                <CardContent>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {form.campos.map(campo => {
                                      const isObligatorio = getCampoObligatorio(moduloKey, formKey, campo.id, campo.default_obligatorio);
                                      const key = `${moduloKey}_${formKey}`;
                                      const hasChange = cambiosPendientes[key]?.[campo.id] !== undefined;
                                      
                                      return (
                                        <div
                                          key={campo.id}
                                          className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                                            isObligatorio 
                                              ? 'bg-green-50 border-green-200' 
                                              : 'bg-slate-50 border-slate-200'
                                          } ${hasChange ? 'ring-2 ring-amber-300' : ''}`}
                                        >
                                          <div className="flex items-center gap-2">
                                            <span className={`font-medium ${isObligatorio ? 'text-green-700' : 'text-slate-600'}`}>
                                              {campo.label}
                                            </span>
                                            {isObligatorio && (
                                              <Badge className="bg-green-100 text-green-700 text-xs">Requerido</Badge>
                                            )}
                                            {hasChange && (
                                              <Badge variant="outline" className="text-xs border-amber-400 text-amber-600">Modificado</Badge>
                                            )}
                                          </div>
                                          <Switch
                                            checked={isObligatorio}
                                            onCheckedChange={() => handleToggleCampo(moduloKey, formKey, campo.id, isObligatorio)}
                                            disabled={!isAdmin}
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </div>
                      </AccordionContent>
                    </Card>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </div>
      </main>
    </div>
  );
}
